import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { url } from "../../api";
import "./image_stream.css";

/*
  Streams OpenAI image generation partial previews into an AOTD-shaped
  frame. The backend (POST /api/images/stream) talks to OpenAI with
  stream:true + partial_images, and forwards the events to us as SSE.

  Wire contract:
    event: status   data: { message }
    event: partial  data: { index, b64 }
    event: complete data: { b64 }
    event: error    data: { message }
    event: done     data: {}

  EventSource only supports GET, so we POST with fetch and read the
  ReadableStream by hand — same SSE wire format, just over POST.
*/

type StreamEvent =
    | { type: "status"; message: string }
    | { type: "partial"; index: number; b64: string }
    | { type: "complete"; b64: string }
    | { type: "error"; message: string }
    | { type: "done" };

type LogEntry = {
    id: number;
    text: string;
    kind: "status" | "partial" | "complete" | "error" | "info";
    at: number;
};

const DEFAULT_PROMPT =
    "A cinematic photo of a tiny robot painting a sunset, dramatic light, fine detail";

type Mode = "live" | "record" | "simulate";

const ImageStreamDemo: FC = () => {
    const [prompt, setPrompt] = useState<string>(DEFAULT_PROMPT);
    const [partials, setPartials] = useState<number>(2);
    const [mode, setMode] = useState<Mode>("live");
    const [running, setRunning] = useState<boolean>(false);
    const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
    const [previewIndex, setPreviewIndex] = useState<number | null>(null);
    const [isFinal, setIsFinal] = useState<boolean>(false);
    const [log, setLog] = useState<LogEntry[]>([]);
    const abortRef = useRef<AbortController | null>(null);
    const logIdRef = useRef<number>(0);
    const logScrollRef = useRef<HTMLDivElement | null>(null);

    const pushLog = useCallback((kind: LogEntry["kind"], text: string) => {
        setLog((prev) => {
            const next = [...prev, { id: ++logIdRef.current, kind, text, at: Date.now() }];
            return next.slice(-80);
        });
    }, []);

    useEffect(() => {
        const node = logScrollRef.current;
        if (node) node.scrollTop = node.scrollHeight;
    }, [log]);

    useEffect(() => () => abortRef.current?.abort(), []);

    const cancel = useCallback(() => {
        abortRef.current?.abort();
        abortRef.current = null;
        setRunning(false);
    }, []);

    const handleEvent = useCallback(
        (evt: StreamEvent) => {
            if (evt.type === "status") {
                pushLog("status", evt.message);
                return;
            }
            if (evt.type === "partial") {
                setImageDataUrl(`data:image/png;base64,${evt.b64}`);
                setPreviewIndex(evt.index);
                setIsFinal(false);
                pushLog("partial", `preview #${evt.index + 1} rendered`);
                return;
            }
            if (evt.type === "complete") {
                setImageDataUrl(`data:image/png;base64,${evt.b64}`);
                setIsFinal(true);
                pushLog("complete", "final image rendered");
                return;
            }
            if (evt.type === "error") {
                pushLog("error", evt.message);
                return;
            }
            if (evt.type === "done") {
                pushLog("info", "stream closed");
            }
        },
        [pushLog],
    );

    const consumeSse = useCallback(
        async (response: Response, onEvent: (evt: StreamEvent) => void) => {
            if (!response.body) throw new Error("response has no body");
            const reader = response.body.getReader();
            const decoder = new TextDecoder("utf-8");
            let buffer = "";

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });

                let sepIdx = buffer.indexOf("\n\n");
                while (sepIdx !== -1) {
                    const raw = buffer.slice(0, sepIdx);
                    buffer = buffer.slice(sepIdx + 2);
                    sepIdx = buffer.indexOf("\n\n");

                    let eventName = "message";
                    const dataLines: string[] = [];
                    for (const line of raw.split("\n")) {
                        if (!line || line.startsWith(":")) continue;
                        const colon = line.indexOf(":");
                        const field = colon === -1 ? line : line.slice(0, colon);
                        const value =
                            colon === -1 ? "" : line.slice(colon + 1).replace(/^\s/, "");
                        if (field === "event") eventName = value;
                        else if (field === "data") dataLines.push(value);
                    }
                    if (dataLines.length === 0) continue;

                    let payload: any = {};
                    try {
                        payload = JSON.parse(dataLines.join("\n"));
                    } catch {
                        continue;
                    }

                    if (eventName === "status" && typeof payload.message === "string") {
                        onEvent({ type: "status", message: payload.message });
                    } else if (
                        eventName === "partial" &&
                        typeof payload.b64 === "string" &&
                        typeof payload.index === "number"
                    ) {
                        onEvent({ type: "partial", index: payload.index, b64: payload.b64 });
                    } else if (eventName === "complete" && typeof payload.b64 === "string") {
                        onEvent({ type: "complete", b64: payload.b64 });
                    } else if (eventName === "error" && typeof payload.message === "string") {
                        onEvent({ type: "error", message: payload.message });
                    } else if (eventName === "done") {
                        onEvent({ type: "done" });
                    }
                }
            }
        },
        [],
    );

    const start = useCallback(async () => {
        const trimmed = prompt.trim();
        if (mode !== "simulate" && !trimmed) {
            pushLog("error", "prompt is empty");
            return;
        }
        if (running) return;

        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setRunning(true);
        setImageDataUrl(null);
        setPreviewIndex(null);
        setIsFinal(false);
        setLog([]);
        pushLog("info", `opening stream (mode=${mode})...`);

        try {
            const response = await fetch(`${url}/api/images/stream`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    prompt: trimmed,
                    partialImages: partials,
                    mode,
                }),
                signal: controller.signal,
                credentials: "include",
            });

            if (!response.ok) {
                const text = await response.text().catch(() => "");
                pushLog("error", `HTTP ${response.status}: ${text.slice(0, 200)}`);
                setRunning(false);
                return;
            }

            await consumeSse(response, handleEvent);
        } catch (err: any) {
            if (err?.name === "AbortError") {
                pushLog("info", "cancelled");
            } else {
                pushLog("error", err?.message || "stream failed");
            }
        } finally {
            setRunning(false);
            abortRef.current = null;
        }
    }, [prompt, partials, mode, running, handleEvent, consumeSse, pushLog]);

    return (
        <div className="image-stream-page">
            <div className="image-stream-controls">
                <label htmlFor="image-stream-prompt" className="image-stream-label">
                    Prompt
                </label>
                <textarea
                    id="image-stream-prompt"
                    className="image-stream-prompt"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    rows={3}
                    disabled={running}
                    placeholder="describe the image..."
                />

                <div className="image-stream-row">
                    <label htmlFor="image-stream-partials" className="image-stream-label-inline">
                        Partial previews (0-3)
                    </label>
                    <input
                        id="image-stream-partials"
                        type="number"
                        min={0}
                        max={3}
                        value={partials}
                        onChange={(e) =>
                            setPartials(Math.max(0, Math.min(3, Number(e.target.value) || 0)))
                        }
                        disabled={running || mode === "simulate"}
                        className="image-stream-partials"
                    />

                    <label htmlFor="image-stream-mode" className="image-stream-label-inline">
                        Mode
                    </label>
                    <select
                        id="image-stream-mode"
                        className="image-stream-mode"
                        value={mode}
                        onChange={(e) => setMode(e.target.value as Mode)}
                        disabled={running}
                    >
                        <option value="live">Live (call OpenAI)</option>
                        <option value="record">Record (live + save fixture)</option>
                        <option value="simulate">Simulate (replay fixture)</option>
                    </select>

                    {running ? (
                        <button type="button" className="image-stream-btn image-stream-btn-cancel" onClick={cancel}>
                            Cancel
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="image-stream-btn image-stream-btn-go"
                            onClick={start}
                            disabled={mode !== "simulate" && !prompt.trim()}
                        >
                            {mode === "simulate" ? "Replay" : mode === "record" ? "Generate + Record" : "Generate"}
                        </button>
                    )}
                </div>
            </div>

            <div
                className={`image-stream-frame${imageDataUrl ? " has-image" : ""}${
                    running ? " is-running" : ""
                }`}
            >
                {imageDataUrl ? (
                    <img
                        src={imageDataUrl}
                        alt={isFinal ? "final" : `preview ${previewIndex !== null ? previewIndex + 1 : ""}`}
                        className="image-stream-image"
                    />
                ) : (
                    <div className="image-stream-empty">
                        {running ? "" : "press Generate to start"}
                    </div>
                )}

                {(running || log.length > 0) && (
                    <div className="image-stream-log" ref={logScrollRef}>
                        {log.map((entry) => (
                            <div
                                key={entry.id}
                                className={`image-stream-log-line image-stream-log-${entry.kind}`}
                            >
                                <span className="image-stream-log-kind">{entry.kind}</span>
                                <span className="image-stream-log-text">{entry.text}</span>
                            </div>
                        ))}
                    </div>
                )}

                <div className="image-stream-caption">
                    {isFinal
                        ? "final image"
                        : previewIndex !== null
                            ? `preview #${previewIndex + 1}`
                            : running
                                ? "waiting for first preview..."
                                : "\u00A0"}
                </div>
            </div>
        </div>
    );
};

export default ImageStreamDemo;
